import type { Translations } from '@/i18n/locales/en';
import { formatMoney } from '@/utils/money';
import type { MenuImportReview } from '@restaurant/api-client';

export type ReviewPage = MenuImportReview['pages'][number];
export type ReviewDish = MenuImportReview['dishes'][number];
export type ReviewFlag = ReviewDish['flags'][number];

export interface ReviewPageView {
  source: ReviewPage;
  dishes: ReviewDish[];
  total: number;
  flagged: number;
}

export const isFlagged = (dish: ReviewDish): boolean => dish.flags.length > 0;

export const flaggedTotal = (review: MenuImportReview): number =>
  review.dishes.filter(isFlagged).length;

export function reviewPages(review: MenuImportReview, flaggedOnly: boolean): ReviewPageView[] {
  return review.pages.map((source) => {
    const all = review.dishes.filter((dish) => dish.page === source.page);
    const flagged = all.filter(isFlagged);
    return {
      source,
      dishes: flaggedOnly ? flagged : all,
      total: all.length,
      flagged: flagged.length,
    };
  });
}

export function firstFlaggedPage(pages: ReviewPageView[]): number {
  return Math.max(
    pages.findIndex((page) => page.flagged > 0),
    0,
  );
}

export function flagText(
  flag: ReviewFlag,
  c: Translations['menuImportReview'],
  currency: string,
): string {
  switch (flag.kind) {
    case 'noPrice':
      return c.noPrice;
    case 'priceOutlier':
      return c.priceOutlier(formatMoney(flag.median, currency));
    case 'unknownAllergenCodes':
      return c.unknownCodes(flag.codes.join(', '));
    case 'itemNumberDropped':
      return c.itemNumberDropped(flag.code);
    case 'pickLimit':
      return c.pickLimit(
        flag.options.map((option) => `${option.name} (${option.count})`).join(', '),
      );
  }
}
